/* eslint-disable react-hooks/exhaustive-deps */
import isObject from 'lodash/isObject';
import React, {
  Fragment, useEffect, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  useTable,
  useSortBy,
  usePagination,
  useExpanded,
  useAsyncDebounce,
} from 'react-table';
import { useDispatch, useSelector } from 'react-redux';
import isPlainObject from 'lodash/isPlainObject';

import { TableSelectors } from '../../redux/selectors';
import { TableActionCreators } from '../../redux/actions/creators';
import ArrowDownward from '../../../assets/arrow_downward.svg';
import ArrowSquareRight from '../../../assets/arrow-square-right.svg';
import { PAGINATION } from '../../../../shared/constants';
import styles from '../../styles/views/components/Table.module.scss';

const Table = (props) => {
  const dispatch = useDispatch();
  const controlledPagination = useSelector(TableSelectors.selectPagination);
  const controlledSortBy = useSelector(TableSelectors.selectSortBy);
  const prevFetchDataRef = useRef();
  const {
    columns,
    data,
    onRowClick,
    caption,
    emptyDataHeading,
    renderCell,
    manualPagination,
    manualSortBy,
    initialState,
    isMultiSortEvent,
    fetchData,
    CustomExpandedRow,
    expandSubRows,
    customRowProps,
    isPaginationHidden,
    extraWrapperStyles,
    colSpan,
    activeElementKey,
  } = props;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    pageCount,
    state: { pageIndex, pageSize, sortBy },
    canPreviousPage,
    canNextPage,
  } = useTable(
    {
      initialState,
      columns,
      data,
      disableSortRemove: true,
      manualPagination,
      manualSortBy,
      isMultiSortEvent,
      pageCount: controlledPagination.pageCount,
      useControlledState: (state) => {
        const controlledState = { ...state };
        const deps = [];
        if (manualPagination) {
          controlledState.pageIndex = controlledPagination.pageIndex;
          controlledState.pageSize = controlledPagination.pageSize;
          deps.push(controlledPagination.pageIndex, controlledPagination.pageSize);
        }
        if (manualSortBy) {
          controlledState.sortBy = controlledSortBy;
          deps.push(controlledSortBy);
        }

        return useMemo(() => controlledState, [state, ...deps]);
      },
      expandSubRows,
    },
    useSortBy,
    useExpanded,
    usePagination,
  );
  const rowsCount = [50, 100, 150];

  useEffect(() => {
    function setInitSortByForManualMode() {
      if (manualSortBy && initialState.sortBy?.length && !sortBy?.length) {
        dispatch(TableActionCreators.setSortBy(initialState.sortBy));
      }
    }

    setInitSortByForManualMode();
  }, []);

  const fetchDeps = [];
  if (manualSortBy) fetchDeps.push(sortBy?.[0]?.id, sortBy?.[0]?.desc);
  const fetchDataDebounced = useAsyncDebounce(fetchData, 100);
  useEffect(() => {
    function resetTablePagination() {
      dispatch(TableActionCreators.setPagination({
        pageIndex: PAGINATION.pageIndex,
        pageSize,
      }));
      prevFetchDataRef.current = fetchData;
    }
    const isFilterChanged = prevFetchDataRef.current !== fetchData;
    if (isFilterChanged) resetTablePagination();

    fetchDataDebounced({ pageIndex, pageSize }, manualSortBy ? sortBy : null);
  }, [dispatch, fetchDataDebounced, fetchData, pageIndex, pageSize, ...fetchDeps]);

  return (
    <div className={styles.wrapper} style={extraWrapperStyles}>
      <div>{caption && <h3 className={styles.tableCaption}>{caption}</h3>}</div>
      <table {...getTableProps()} className={styles.table}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()} className={styles.tableRow}>
              {headerGroup.headers.map((column) => {
                const customToggleSortBy = () => {
                  if (column.canSort) {
                    const sortField = column.id;
                    const isDescSorting = column.isSorted ? !column.isSortedDesc : false;
                    dispatch(TableActionCreators.setSortBy([{
                      id: sortField,
                      desc: isDescSorting,
                    }]));
                    dispatch(TableActionCreators.setPagination({
                      pageIndex: PAGINATION.pageIndex,
                      pageSize,
                    }));
                  }
                };
                const onColumnHeaderClick = manualSortBy ? customToggleSortBy : column.getSortByToggleProps().onClick;
                return (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={styles.tableHeader}
                    style={column.customStyles}
                    onClick={onColumnHeaderClick}
                  >
                    <div
                      className={styles.cellContainer}
                      style={column.customHeaderStyles}
                      data-tip={column.tooltip ? t(`tableColumns.tooltips.${column.tooltip}`, { ns: i18next.Namespace.COMMON }) : ''}
                    >
                      {isObject(column.render('Header'))
                        ? column.render('Header')
                        : t(`tableColumns.${column.render('Header')}`, { ns: i18next.Namespace.COMMON })}
                      {!column.disableSortBy && (
                        <span className={styles.arrowContainer}>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? <ArrowDownward className={styles.arrowDownwardActive} />
                              : <ArrowDownward className={styles.arrowUpwardActive} />
                            : <ArrowDownward className={styles.arrowDownwardInactive} />}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
      </table>
      <div className={styles.scrollContainer}>
        <table {...getTableProps()} className={styles.table}>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              row.customRowProps = customRowProps;
              return (
                <Fragment key={row.id}>
                  <tr
                    {...row.getRowProps()}
                    className={[styles.highlighted, styles.tableRow, (row.original._id === activeElementKey) ? styles.activeRow : ''].join(' ')}
                    onClick={(event) => onRowClick(event, row)}
                  >
                    {row.cells.map(cell => renderCell(cell))}
                  </tr>
                  {CustomExpandedRow && row.isExpanded && <CustomExpandedRow row={row} colSpan={colSpan} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.length === 0 && <div className={styles.emptyDataHeading}>{emptyDataHeading}</div>}
      {data.length !== 0 && manualPagination && !isPaginationHidden && (
        <div className={styles.paginationWrapper}>
          <div className={styles.paginationContent}>
            <div className={styles.paginationPageTitle}>{t('components.page', { ns: i18next.Namespace.COMMON })}</div>
            <ArrowSquareRight
              className={!canPreviousPage ? styles.arrowSquareLeftDisabled : styles.arrowSquareLeftEnabled}
              onClick={() => {
                if (!canPreviousPage) return;
                dispatch(TableActionCreators.setPagination({ pageIndex: pageIndex - 1 }));
              }}
            />
            <div className={styles.paginationPageIndex}>{pageIndex + 1} of {pageCount}</div>
            <ArrowSquareRight
              className={!canNextPage ? styles.arrowSquareRightDisabled : styles.arrowSquareRightEnabled}
              onClick={() => {
                if (!canNextPage) return;
                dispatch(TableActionCreators.setPagination({ pageIndex: pageIndex + 1 }));
              }}
            />
            <div className={styles.paginationRowsCount}>{t('components.rows', { ns: i18next.Namespace.COMMON })}</div>
            <select
              className={styles.paginationRowsSelect}
              value={pageSize}
              onChange={e => dispatch(TableActionCreators.setPagination({ pageSize: e.target.value, pageIndex: PAGINATION.pageIndex }))}
            >
              {rowsCount.map(value => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

Table.getCustomMappedFields = (columnsFixtures) => {
  const result = [];
  Object.keys(columnsFixtures).forEach((columnKey) => {
    const column = columnsFixtures[columnKey];
    if (!column.disableSortBy && isPlainObject(column.customFieldMapping)) {
      result.push(column.customFieldMapping);
    }
  });

  return result;
};

Table.convertSortByForDB = (sortByArray, columnsFixtures) => {
  const mappedFields = Table.getCustomMappedFields(columnsFixtures);
  if (!sortByArray?.length || !mappedFields?.length) return null;

  const [sortColumn] = sortByArray;
  const sortOrder = sortColumn.desc ? -1 : 1;
  const sortField = mappedFields.find(field => field.tableAccessor === sortColumn.id);
  if (!sortField) return null;

  return { [sortField.documentKey]: sortOrder };
};

Table.convertSortByForTable = (sortByObject, columnsFixtures) => {
  const mappedFields = Table.getCustomMappedFields(columnsFixtures);
  if (!isPlainObject(sortByObject) || sortByObject === null || !mappedFields?.length) return [];

  const foundIndex = mappedFields.findIndex(field => Object.prototype.hasOwnProperty.call(sortByObject, field.documentKey));
  if (foundIndex === -1) return [];
  const foundField = mappedFields[foundIndex];

  return [{
    id: foundField.tableAccessor,
    desc: sortByObject[foundField.documentKey] === -1,
  }];
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  onRowClick: PropTypes.func,
  caption: PropTypes.node,
  emptyDataHeading: PropTypes.string,
  renderCell: PropTypes.func.isRequired,
  manualPagination: PropTypes.bool,
  manualSortBy: PropTypes.bool,
  initialState: PropTypes.shape({
    sortBy: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool,
    })),
  }),
  isMultiSortEvent: PropTypes.func,
  fetchData: PropTypes.func,
  CustomExpandedRow: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  expandSubRows: PropTypes.bool,
  customRowProps: PropTypes.object,
  isPaginationHidden: PropTypes.bool,
  extraWrapperStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  colSpan: PropTypes.string.isRequired,
  activeElementKey: PropTypes.string,
};

Table.defaultProps = {
  data: null,
  caption: null,
  onRowClick: () => {},
  emptyDataHeading: '',
  manualPagination: false,
  manualSortBy: false,
  initialState: {},
  isMultiSortEvent: () => false,
  fetchData: () => {},
  CustomExpandedRow: null,
  expandSubRows: true,
  customRowProps: {},
  isPaginationHidden: false,
  extraWrapperStyles: null,
  activeElementKey: '',
};

export default Table;
